import { createAction } from '@reduxjs/toolkit';
import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  retry,
} from '@reduxjs/toolkit/query/react';
import { FETCH_ERRORS } from 'modules/threads/constants';
import { getCookie, handleApiCall } from 'utils/helpers';
import { deepCamelcaseKeys, deepUnderscoreKeys } from 'utils/mappers';
import { userApi } from 'modules/user/userApi';
import { ENV } from '../constants/env';
import { IError } from '../types';
import { AppDispatch, RootState } from './store';

export const resetAll = createAction('reset/all');

// The query that answers "who is signed in". A refusal of *this* query is that
// answer, so it must never be what triggers asking it again.
const USER_QUERY_ENDPOINT = 'getUserInfo';

const redirectToLoginIfNeeded = (dispatch: AppDispatch) => {
  dispatch(userApi.endpoints.getUserInfo.initiate(undefined, { forceRefetch: true }));
  // Reset document title, because we changing it when user has some unread notifications
  document.title = 'Threads';
};

const handleUnauthorizedRequest = (error: IError, endpoint: string) => (dispatch: AppDispatch) => {
  const { status, data } = error.error;
  const detail = (data as { detail?: string } | undefined)?.detail;
  if (!status || !detail) {
    // eslint-disable-next-line no-console
    console.error('Invalid error object received:', error);
  }

  switch (status) {
    case 401: {
      if (detail === FETCH_ERRORS.INVALID_TOKEN) {
        dispatch(resetAll());
      }
      break;
    }
    case 403: {
      if (detail === FETCH_ERRORS.INVALID_COOKIE) {
        dispatch(resetAll());
        // Re-reading the user is how the app notices it has been signed out;
        // asking again because that very question was refused only repeats the
        // answer it already has - and emptied the app on every refusal, which
        // is what rebuilt the SSO callback page under a spent code.
        if (endpoint !== USER_QUERY_ENDPOINT) {
          redirectToLoginIfNeeded(dispatch);
        }
      }
      break;
    }
    default:
      // eslint-disable-next-line no-console
      console.warn(`Unhandled error status: ${status}`);
  }
};

const BASE_API = ENV.VITE_API_URL || '';
const EXCLUDE_ENDPOINTS = ['getStatistic'];

const baseQuery = fetchBaseQuery({
  baseUrl: BASE_API,
  prepareHeaders: (headers, { getState }) => {
    const {
      userInfo: { siteId },
    } = (getState() as RootState).user;

    // Only set site-id when we have a valid value; otherwise backend uses request.user.site
    if (siteId != null && siteId !== '') {
      headers.set('site-id', String(siteId));
    }
    const csrf = getCookie('csrftoken');
    if (csrf) headers.set('X-CSRFToken', csrf);
    return headers;
  },
  credentials: 'include',
});

const baseQueryWithRetry = retry(baseQuery, { maxRetries: 0 });

const baseQueryWithCamelize: typeof baseQueryWithRetry = async (args, api, extraOptions = {}) => {
  const result = await baseQueryWithRetry(deepUnderscoreKeys(args), api, extraOptions);
  handleApiCall(result, (error) => api.dispatch(handleUnauthorizedRequest(error, api.endpoint)));
  if (result.data && !EXCLUDE_ENDPOINTS.includes(api.endpoint)) {
    result.data = deepCamelcaseKeys(result.data);
  }
  return result;
};
export const api = createApi({
  /**
   * `reducerPath` is optional and will not be required by most users.
   * This is useful if you have multiple API definitions,
   * e.g. where each has a different domain, with no interaction between endpoints.
   * Otherwise, a single API definition should be used in order to support tag invalidation,
   * among other features
   */
  reducerPath: 'splitApi',
  /**
   * A bare bones base query would just be `baseQuery: fetchBaseQuery({ baseUrl: '/' })`
   */
  baseQuery: baseQueryWithCamelize,
  /**
   * Tag types must be defined in the original API definition
   * for any tags that would be provided by injected endpoints
   */
  tagTypes: [
    'threadList',
    'threadListFilteredByRelatedThreads',
    'user',
    'users',
    'chatMessages',
    'userInvites',
    'contributorsList',
    'contributorsInvites',
    'events',
    'resources',
    'relatedThreads',
    'searchRelatedThreads',
    'user-info',
    'user-groups',
    'user-preferences',
    'statistic',
    'notifications',
    'threadGantt',
    'user-notification-settings',
    'currentSiteSubscription',
    'userNonActiveIntegrations',
    'subscriptionsPlans',
    'userAvailableIntegrations',
    'playablePing',
    'plyableAnnotations',
    'joinRequestsList',
    'threads3dAnnotations',
    'threads3dAnnotation',
    'references',
    'reference-versions',
    'copilotSuggestions',
    'workspace',
    'threadViewability',
    'tdps',
    'tdpTemplates',
    'templateSections',
    'threadTags',
    'oauthProviders',
    'oauthStatus',
  ],
  /**
   * This api has endpoints injected in adjacent files,
   * which is why no endpoints are shown below.
   * If you want all endpoints defined in the same file, they could be included here instead
   */
  endpoints: () => ({}),
});

/**
 * Returns the response exactly how it is in the API
 * No camel casing.
 */
export const apiRaw = createApi({
  reducerPath: 'apiRaw',
  baseQuery: baseQueryWithRetry as BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
  tagTypes: ['drawings', 'boms'],
  endpoints: () => ({}),
});
